import React, { useState, useCallback } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '@/store';
import { authApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';

// Web/desktop-only. Persistent banner shown while an admin is impersonating a
// user. Mounted once in (app)/_layout.web.jsx. The banner is the single exit
// ramp back to the admin session — it is deliberately loud and always-on-top
// because impersonation grants FULL access to the target's real account.
//
// Native platforms never reach this component: the handoff is only issued from
// the web admin dashboard, and app/impersonate.jsx bounces native to /app.
export default function ImpersonationBanner() {
  const router = useRouter();
  const isImpersonating = useAuthStore(state => state.isImpersonating);
  const targetEmail = useAuthStore(state => state.user?.email);
  const stopImpersonation = useAuthStore(state => state.stopImpersonation);
  const [stopping, setStopping] = useState(false);

  const handleStop = useCallback(async () => {
    if (stopping) return;
    setStopping(true);

    // Read the current (target) refresh token BEFORE stopImpersonation swaps
    // state out from under us.
    const targetRefreshToken = useAuthStore.getState().refreshToken;

    // Best-effort server-side revoke + audit log. Never block the local
    // restore on this — the admin needs to get back to their session even if
    // the network call fails.
    try {
      await authApi.impersonateStop(targetRefreshToken);
    } catch (_) {
      // Swallow: local restore is the source of truth for "stop".
    }

    const { restored, returnUrl } = stopImpersonation();

    if (!restored) {
      // No SPA admin session to restore (fresh-tab impersonation). Send the
      // browser back to the admin dashboard, which is session-cookie authed.
      if (typeof window !== 'undefined' && returnUrl) {
        window.location.href = returnUrl;
        return;
      }
      router.replace('/');
      return;
    }

    // Admin session restored in-app.
    setStopping(false);
    router.replace('/app');
  }, [stopping, stopImpersonation, router]);

  // Defensive: only render on web. The layout only mounts this on web, but
  // keep the guard so the component is safe to import anywhere.
  if (Platform.OS !== 'web' || !isImpersonating) {
    return null;
  }

  return (
    <View style={styles.banner} accessibilityRole="alert">
      <View style={styles.content}>
        <Text size="n" weight="semibold" color="white" style={styles.message}>
          <Text size="n" weight="bold" color="white">⚠ Impersonating</Text>
          {' '}
          {targetEmail || 'a user'}
          {' '}
          <Text size="n" color="white" style={styles.subdued}>
            — full access is enabled. All actions (chats, purchases, settings) are real and affect this account.
          </Text>
        </Text>
      </View>
      <Button
        text={stopping ? 'Stopping…' : 'Stop impersonation'}
        onPress={handleStop}
        isDisabled={stopping}
        variant="danger"
        size="small"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 99999,
    elevation: 99999,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#b45309', // amber-700 — distinct from the normal UI palette
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  message: {
    lineHeight: 18,
  },
  subdued: {
    opacity: 0.9,
  },
});
